<template>
  <div
    v-show="showMobileMenu || $screen !== 's'"
    :class="[
      $style.base,
      {
        [$style.isSmall]: $screen === 's',
        [$style.expanded]: isNavExpanded
      }
    ]"
    v-test="'the-main-nav'"
  >
    <BlockedInfoModal
      v-if="hasBlockedInfoModal"
      @close="hasBlockedInfoModal = false"
    />
    <transition name="slide-down">
      <MobileOverlay
        v-if="$screen === 's'"
        @navItemClick="onNavItemClick"
        v-test="'nav-main-mobile-overlay'"
      />
      <div
        v-else
        :class="$style.inner"
      >
        <div :class="$style.section">
          <NavItem
            :link="{
              routeName: 'dashboard',
              tooltip: $t('nav.dashboard')
            }"
            dark
            @click="onNavItemClick"
          >
            <div :class="$style.logo">
              <BaseLogo />
            </div>
          </NavItem>
          <div :class="$style.mainItems">
            <NavItem
              v-for="(link, index) in links"
              :key="index"
              :link="link"
              :tooltip="
                isNavExpanded
                  ? undefined
                  : {
                      position: 'right',
                      text: link.tooltip
                    }
              "
              @click="onNavItemClick"
            />
          </div>
        </div>
        <Expand
          :tooltip="{
            position: 'right',
            text: isNavExpanded
              ? $t('global.actions.collapse')
              : $t('global.actions.expand')
          }"
        />
        <div
          v-if="!company.blocked"
          :class="$style.section"
        >
          <NavItem
            v-if="showMobileDesktopButton"
            :link="{
              icon: isMobileDesktop ? 'mobile' : 'monitor',
              tooltip: $t('nav.mobile')
            }"
            @click="toggleMobileDesktop"
          />
          <NavItem
            v-if="isRouteAllowed('admin')"
            :link="{
              icon: 'settings',
              routeName: 'admin',
              tooltip: $t('nav.settings')
            }"
            :tooltip="
              isNavExpanded
                ? undefined
                : {
                    position: 'right',
                    text: $t('nav.settings')
                  }
            "
            @click="onNavItemClick"
          />
          <div :class="$style.whatsNew">
            <NavItem
              ref="whatsNewNavItem"
              :link="{
                icon: 'bulb',
                name: 'whats-new',
                tooltip: $t('nav.whats_new'),
                active: isNewsWidgetOpen,
                notification: {
                  show: unreadNewsCount > 0,
                  value: unreadNewsCount
                }
              }"
              :tooltip="
                isNavExpanded
                  ? undefined
                  : {
                      position: 'right',
                      text: $t('nav.whats_new')
                    }
              "
              @click="onNavItemClick"
              v-test="'nav-new-features'"
            />
          </div>
          <NavItem
            v-if="!browser.isUIWebview"
            ref="navItemInfo"
            :link="{
              icon: 'info',
              routeName: 'support-hub',
              tooltip: $t('nav.support_hub')
            }"
            :tooltip="
              isNavExpanded
                ? undefined
                : {
                    position: 'right',
                    text: $t('nav.support_hub')
                  }
            "
            @click="onNavItemClick"
            v-test="'nav-support'"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia';
import { defineComponent } from 'vue';
import dayjs from '@/dayjs';
import productFruitsService, {
  isNewsWidgetOpen,
  unreadNewsCount,
} from '@/product-fruits';
import { isRouteAllowed } from '@/router/helpers';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useNativeAppStore } from '@/stores/native-app';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useTreatwellStore } from '@/stores/treatwell';
import { useUserStore } from '@/stores/user';
import { useVersionMismatchStore } from '@/stores/version-mismatch';
import { BlockReason } from '@/types';
import { browser, device } from '@/user-context';
import BlockedInfoModal from './BlockedInfoModal.vue';
import Expand from './Expand.vue';
import MobileOverlay from './mobile/MobileOverlay.vue';
import NavItem from './NavItem.vue';
import useOnline from './online';

export default defineComponent({
  components: {
    NavItem,
    BlockedInfoModal,
    Expand,
    MobileOverlay,
  },
  setup() {
    const { hideOnlineRoutes, showOnlineRoutesNotification } = useOnline();
    return {
      browser,
      isRouteAllowed,
      hideOnlineRoutes,
      showOnlineRoutesNotification,
      unreadNewsCount,
      isNewsWidgetOpen,
    };
  },
  data() {
    return {
      hasBlockedInfoModal: false,
    };
  },
  watch: {
    $screen() {
      this.$nextTick(() => {
        this.setNavbarWidth();
        this.attachNewsWidget();
      });
    },
    isNavExpanded() {
      setTimeout(() => {
        this.setNavbarWidth();
      }, 500);
    },
    isMobileDesktop: {
      handler(newValue, oldValue) {
        const pageLayoutStore = usePageLayoutStore();

        if (!device.mobile) {
          pageLayoutStore.isMobileDesktop = false;
          return;
        }

        const tag = document.querySelector('meta[name="viewport"]');

        if (newValue) {
          // Set user-scalable=no to reset the "zoom", and then remove it to allow the user to zoom in again.
          tag.content = 'width=920,initial-scale=0.38,user-scalable=no';
          setTimeout(() => {
            tag.content = 'width=920,initial-scale=0.38';
          }, 0);

          this.setScreenSize(920);
        } else if (oldValue) {
          tag.content = 'width=device-width,initial-scale=1.0,user-scalable=no';
          this.setScreenSize();
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(useVersionMismatchStore, ['versionMismatchReload']),
    ...mapState(useLocationsStore, ['locationId']),
    ...mapState(usePageLayoutStore, [
      'showMobileMenu',
      'isMobileDesktop',
      'isNavExpanded',
    ]),
    ...mapState(useUserStore, ['user', 'hasFeatureFlag']),
    ...mapState(useCompanyStore, ['company', 'isTrial']),
    ...mapState(useTreatwellStore, [
      'showTWOnboardingNotification',
      'showTreatwell',
    ]),
    ...mapState(useNativeAppStore, ['isNativeApp']),
    marketingTrialHasEnded() {
      return this.company.marketingTrialEndsAt < dayjs().format();
    },
    showMobileDesktopButton() {
      if (!this.hasFeatureFlag('mobile-desktop-button')) {
        return false;
      }

      return device.mobile && (!browser.isUIWebview || browser.isAndroid);
    },
    stockWarningCount() {
      let warningCount = 0;
      if (this.company?.stockWarnings) {
        const warning = this.company.stockWarnings.find(
          (warning) => warning.location_id === this.locationId,
        );
        if (warning) {
          warningCount = warning.count;
        }
      }
      return warningCount;
    },
    links() {
      const links = [
        {
          icon: 'calendar',
          routeName: 'calendar',
        },
        {
          icon: 'people',
          routeName: 'customers',
        },
        {
          icon: 'shopping-cart',
          routeName: 'register',
        },
        {
          icon: 'pricetag',
          routeName: 'products',
          notification: {
            show: this.stockWarningCount > 0,
            value: this.stockWarningCount,
          },
        },
        {
          icon: 'email',
          routeName: 'messages',
        },
        {
          icon: 'message',
          routeName: 'feedback',
          notification: {
            show: this.user.feedbacks > 0,
            value: this.user.feedbacks,
          },
        },
        {
          icon: 'activity',
          routeName: 'reports',
          tooltip: 'reports',
        },
        {
          icon: 'marketing',
          routeName: 'marketing',
        },
        {
          icon: 'flash',
          routeName: 'online',
          tooltip: 'online',
          hide: this.hideOnlineRoutes,
          notification: {
            show: this.showOnlineRoutesNotification,
            value: '',
          },
        },
      ].map((link) => {
        const route = this.$router.resolve({
          name: link.routeName,
        });

        return {
          ...link,
          tooltip: this.$t(
            `nav.${link.tooltip ? link.tooltip : link.routeName}`,
          ),
          show: !!route && !link.hide && isRouteAllowed(route),
        };
      });

      return links.filter((link) => link.show);
    },
  },
  methods: {
    ...mapActions(usePageLayoutStore, ['setScreenSize']),
    redirect(url) {
      if (window.location.port) {
        url = url.replace(/:[\d*]+/, '');
        url = `${url}:${window.location.port}`;
      }
      window.location.replace(url + window.location.pathname);
    },
    onNavItemClick() {
      if (
        this.company.blocked &&
        this.company.blockReason !== BlockReason.PausedCollection
      ) {
        this.hasBlockedInfoModal = true;
        return;
      }
    },
    setNavbarWidth() {
      const pageLayoutStore = usePageLayoutStore();
      pageLayoutStore.navbarWidth =
        this.$screen === 's' ? 0 : this.$el.offsetWidth;
    },
    toggleMobileDesktop() {
      const pageLayoutStore = usePageLayoutStore();
      pageLayoutStore.isMobileDesktop = !this.isMobileDesktop;
    },
    attachNewsWidget() {
      if (this.$refs.whatsNewNavItem) {
        productFruitsService.attachNewsWidget(
          (this.$refs.whatsNewNavItem as { $el: HTMLElement }).$el,
        );
      }
    },
  },
  mounted() {
    this.setNavbarWidth();
    this.attachNewsWidget();
  },
});
</script>

<style
  lang="scss"
  module
>
.tooltip {
  white-space: nowrap;
}

.base {
  position: fixed;
  left: 0;
  top: 0;
  width: $nav-width;
  height: 100%;
  z-index: 220;
  transition: width 0.3s $easeOutExpo;

  &.isSmall {
    top: $nav-width;
    height: calc(100% - #{$nav-width});
    width: 100%;
  }

  &.expanded {
    width: $nav-width-expanded;
  }

  @media print {
    display: none;
  }
}

.inner {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  @include theme('light') {
    background-color: var(--color-primary);
  }

  // Same surface as the cards in dark mode, with a border to separate it from
  // the page. A border rather than an inset shadow, so item hovers don't
  // paint over it.
  @include theme('dark') {
    background-color: var(--surface-primary);
    border-right: 1px solid var(--border);
  }

  .base.isSmall & {
    &:global(.slide-down-leave-active),
    &:global(.slide-down-enter-active) {
      transition: transform 0.3s ease;
    }
    &:global(.slide-down-enter),
    &:global(.slide-down-leave-to) {
      transform: translateY(-100%);
    }
  }
}

.section {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.menu {
  position: absolute;
  top: 0;
  right: $spacing * 0.5;
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: $nav-width * 0.25;
}

.mainItems {
  width: 100%;

  :global(.intercom-namespace) {
    // Hacky way to make the intercom icon stick to the right
    // So that it stays right when expanding the navbar
    
    width: 100% !important;
    max-width: 100% !important;

    & > * {
      left: auto !important;
      right: 5% !important;
    }
  }
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 100%;
  flex-shrink: 0;
}

.infoNav {
  left: $nav-width;
  bottom: 4px;

  .isSmall & {
    bottom: 1px;
  }
}

.whatsNew {
  position: relative;
  width: 100%;
}
</style>
